import GenericCalculator from "../components/GenericCalculator";
import { Ruler, Box } from "lucide-react";
import { BP_THEME, COLOUR_MAP, BlueprintDefs, BlueprintFrame, DimH, DimV, BlueprintFooter } from "../components/blueprint";

function StraightRampPreview({ width = 1000, depth = 1000, left_rise = 100, right_rise = 100, colour = "Natural Black", left_kerb = false, right_kerb = false, curved = false }) {
  const CW = 560, CH = 320;
  const PAD_L = 70, PAD_R = 40, PAD_T = 50, PAD_B = 80;
  const W = Math.max(Number(width) || 0, 100);
  const D = Math.max(Number(depth) || 0, 100);
  const innerW = CW - PAD_L - PAD_R, innerH = CH - PAD_T - PAD_B;
  const scale = Math.min(innerW / W, innerH / D);
  const sw = W * scale, sd = D * scale;
  const startX = PAD_L + (innerW - sw) / 2;
  const topY = PAD_T;
  const fill = COLOUR_MAP[colour] || "#1f2330";
  const ID = "sr";
  return (
    <svg viewBox={`0 0 ${CW} ${CH}`} className="w-full h-auto" style={{ maxHeight: 320, background: BP_THEME.bg, borderRadius: 6 }} data-testid="straight-preview">
      <BlueprintDefs id={ID} fill={fill} />
      <BlueprintFrame id={ID} w={CW} h={CH} title="PLAN VIEW · STRAIGHT RAMP" />
      <rect x={startX} y={topY} width={sw} height={sd} fill={`url(#fill-${ID})`} stroke={BP_THEME.accent} strokeWidth="1.5" rx={curved ? 14 : 0} />
      {left_kerb && <rect x={startX - 5} y={topY - 4} width={5} height={sd + 8} fill={BP_THEME.accent} />}
      {right_kerb && <rect x={startX + sw} y={topY - 4} width={5} height={sd + 8} fill={BP_THEME.accent} />}
      <DimH id={ID} x1={startX} x2={startX + sw} y={topY + sd + 28} label={`W = ${Math.round(W)} mm`} />
      <DimV id={ID} x={startX - 22} y1={topY} y2={topY + sd} label={`D = ${Math.round(D)} mm`} />
      <text x={startX + 6} y={topY - 8} fontSize="9" fill={BP_THEME.accent} fontFamily={BP_THEME.fontMono}>⌀ L.Rise {Math.round(left_rise)}mm</text>
      <text x={startX + sw - 6} y={topY - 8} fontSize="9" fill={BP_THEME.accent} fontFamily={BP_THEME.fontMono} textAnchor="end">⌀ R.Rise {Math.round(right_rise)}mm</text>
      <BlueprintFooter w={CW} y={CH - 12} left={`MATERIAL · RUBBER · ${colour.toUpperCase()}${curved ? "  ·  CURVED" : ""}`} />
    </svg>
  );
}

const FIELD_GROUPS = [
  {
    title: "Dimensions (mm)", icon: Ruler,
    fields: [
      { key: "width", label: "Width", type: "number" },
      { key: "depth", label: "Depth", type: "number" },
      { key: "left_rise", label: "Left Rise", type: "number" },
      { key: "right_rise", label: "Right Rise", type: "number" },
    ],
  },
  {
    title: "Options", icon: Box,
    fields: [
      { key: "left_kerb", label: "Left Kerb", type: "yn" },
      { key: "right_kerb", label: "Right Kerb", type: "yn" },
      { key: "wvs_left", label: "WVS Left", type: "yn" },
      { key: "wvs_right", label: "WVS Right", type: "yn" },
      { key: "wvs_top", label: "WVS Top", type: "yn" },
      { key: "wvs_bottom", label: "WVS Bottom", type: "yn" },
      { key: "notches", label: "Notches", type: "number" },
      { key: "curved", label: "Curved", type: "yn" },
      { key: "quantity", label: "Quantity", type: "number" },
      { key: "colour", label: "Colour", type: "colour" },
    ],
  },
];

const DEFAULTS = {
  width: "", depth: "", left_rise: "", right_rise: "",
  left_kerb: false, right_kerb: false,
  wvs_left: false, wvs_right: false, wvs_top: false, wvs_bottom: false,
  notches: "", curved: false, quantity: 1, colour: "Natural Black",
};

export default function StraightRampCalc() {
  return (
    <GenericCalculator
      productType="straight_ramp"
      productLabel="Straight Rubber Ramp Calculator"
      defaultInputs={DEFAULTS}
      fieldGroups={FIELD_GROUPS}
      PreviewSvg={StraightRampPreview}
      endpoint="/calculate/straight_ramp"
    />
  );
}
